import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { CommonService } from '../common.service';
import { environment } from '../../../environments/environment';
import { ToasterService } from 'angular2-toaster';

export interface ComparisonProduct {
  productId: string;
  productSource: 'sage' | 'promostandards' | 'apparel';
  productData?: any;   // Lightweight snapshot: PrName, ThumbPicLink, ItemNum, CompanyName
}

@Injectable({ providedIn: 'root' })
export class ComparisonService {

  private readonly MAX_ITEMS = 4;

  // In-memory list of products the user has ticked
  private _items$ = new BehaviorSubject<ComparisonProduct[]>([]);
  public items$ = this._items$.asObservable();

  // Controls visibility of the sticky bottom bar
  private _barVisible$ = new BehaviorSubject<boolean>(false);
  public barVisible$ = this._barVisible$.asObservable();

  constructor(
    private _http: HttpClient,
    private _cs: CommonService,
    private _toast: ToasterService
  ) {}

  // ── Getters ──────────────────────────────────────────────────────────────

  get items(): ComparisonProduct[] {
    return this._items$.getValue();
  }

  isSelected(productId: string): boolean {
    return this.items.some(p => p.productId === productId);
  }

  get count(): number {
    return this.items.length;
  }

  // ── Mutation ─────────────────────────────────────────────────────────────

  toggle(product: ComparisonProduct): boolean {
    const current = this.items;
    const idx = current.findIndex(p => p.productId === product.productId);

    if (idx > -1) {
      // Already selected — remove it
      const updated = current.filter(p => p.productId !== product.productId);
      this._items$.next(updated);
      if (updated.length === 0) { this._barVisible$.next(false); }
      return false; // removed
    } else {
      // Not selected — add it
      if (current.length >= this.MAX_ITEMS) {
        this._toast.pop('warning', 'Maximum 4 products can be compared at once. Remove one to add another.');
        return false;
      }
      const updated = [...current, product];
      this._items$.next(updated);
      this._barVisible$.next(true);
      return true; // added
    }
  }

  remove(productId: string): void {
    const updated = this.items.filter(p => p.productId !== productId);
    this._items$.next(updated);
    if (updated.length === 0) { this._barVisible$.next(false); }
  }

  clear(): void {
    this._items$.next([]);
    this._barVisible$.next(false);
  }

  hideBar(): void {
    this._barVisible$.next(false);
  }

  showBar(): void {
    if (this.items.length > 0) { this._barVisible$.next(true); }
  }

  // ── API calls ─────────────────────────────────────────────────────────────

  getSavedComparisons() {
    return this._http.get(environment.API_URL + 'product-comparison', this._cs.setHeader());
  }

  saveComparison(payload: { name: string; products: any[] }) {
    return this._http.post(environment.API_URL + 'product-comparison/save', payload, this._cs.setHeader());
  }

  getComparison(id: number | string) {
    return this._http.get(environment.API_URL + 'product-comparison/get/' + id, this._cs.setHeader());
  }

  updateComparison(id: number | string, payload: { name: string; products: any[] }) {
    return this._http.post(environment.API_URL + 'product-comparison/update/' + id, payload, this._cs.setHeader());
  }

  deleteComparison(id: number | string) {
    return this._http.get(environment.API_URL + 'product-comparison/delete/' + id, this._cs.setHeader());
  }

  generateShareLink(id: number | string) {
    return this._http.get(environment.API_URL + 'product-comparison/generate-share/' + id, this._cs.setHeader());
  }

  getPublicComparison(token: string, franchiseId?: string) {
    let url = environment.API_URL + 'product-comparison/share/' + token;
    if (franchiseId) { url += '?franchise_id=' + franchiseId; }
    return this._http.get(url);
  }

  exportPdf(id: number | string, productDataMap: any) {
    return this._http.post(
      environment.API_URL + 'product-comparison/export-pdf/' + id,
      { product_data_map: productDataMap },
      { ...this._cs.setHeader(), responseType: 'blob' as 'blob' }
    );
  }

  /**
   * Upload a user-supplied image for a product column in a comparison.
   * Returns an Observable that resolves to { data: { url: string } }.
   * Max 5 MB, accepted MIME types: jpeg, png, gif, webp.
   */
  uploadComparisonImage(file: File) {
    const formData = new FormData();
    formData.append('image', file, file.name);
    // Extract the franchise ID from setHeader()'s plain-object result and build
    // a real HttpHeaders instance — omitting Content-Type so the browser sets
    // the multipart boundary automatically.
    const base = this._cs.setHeader();
    const franchiseId = base && base.headers ? base.headers['awi-franchise-id'] : '';
    const headers = new HttpHeaders({ 'awi-franchise-id': franchiseId || '' });
    return this._http.post(
      environment.API_URL + 'product-comparison/upload-image',
      formData,
      { headers }
    );
  }
}

